import { Vehicle } from "../inventory/inventory";

export type LeadStatus =
  | "new"
  | "inProgress"
  | "waitingFinance"
  | "closed"
  | "lost";

export interface EmailHistory {
  id: string;
  type: 'suggested' | 'linked';
  sentAt: string;
  recipientEmail: string;
}

export interface TruckType {
  type: string[];
  size: string[];
  condition: Vehicle["condition"];
  quantity: number;
  make?: string[];
  model?: string[];
  minYear?: number;
  fuelType?: string[];
  maxMileage?: number;
  budget?: number;
}

export interface TruckRequest {
  notes?: string;
  types?: TruckType[];
}

export type LeadBusinessContactRole = "owner" | "manager" | "partner";

/** Optional secondary contact at the company (manual entry; not a portal client). */
export interface LeadBusinessContact {
  name?: string;
  email?: string;
  phone?: string;
  role?: LeadBusinessContactRole;
}

export interface FollowUpNote {
  id: string;
  text: string;
  author: string;
  createdAt: string;
}

export interface StatusHistory {
  id: string;
  previousStatus: LeadStatus | null;
  newStatus: LeadStatus;
  changedBy: string;
  changedAt: Date;
  notes?: string;
}

/** Maps linked vehicle id → `pickup_locations` doc id for that truck's dropoff. */
export type LeadTruckDropoffLocationIds = Record<string, string>;

export const normalizeLeadTruckDropoffLocationIds = (
  raw: LeadTruckDropoffLocationIds | undefined | null,
): LeadTruckDropoffLocationIds => {
  if (!raw || typeof raw !== "object") return {};
  const result: LeadTruckDropoffLocationIds = {};
  for (const [vehicleId, locationId] of Object.entries(raw)) {
    const truckId = vehicleId.trim();
    const id = typeof locationId === "string" ? locationId.trim() : "";
    if (truckId && id) result[truckId] = id;
  }
  return result;
};

export interface Lead {
  id: string;
  /** Human-readable identifier, e.g. `L26-00042`. Assigned once at creation. */
  leadNumber?: string | null;
  customerName: string;
  email: string;
  phone: string;
  /**
   * Portal client user id (`PrivateUserInfoType.id`) linked to this lead.
   * Set when a client is selected in the lead form; prefer over email/name matching.
   */
  client_id?: string | null;
  /**
   * CRM `contacts` doc id. Permanent person key (same pattern as inventory leads).
   * Prefer over email/phone soft match when attaching leads to a contact.
   */
  contactId?: string | null;
  /** Additional business contact (all fields optional). */
  businessContact?: LeadBusinessContact;
  status: LeadStatus | null;
  prevStatus: LeadStatus | null;
  assignedRepId: number;
  company: string;
  /** `contact_companies` doc id when the lead was created from a CRM company. */
  companyId?: string | null;
  /** State abbreviation; kept for filters and notifications. */
  location: string;
  /**
   * @deprecated Legacy lead-level dropoff. New leads use per-truck dropoffs in
   * `truckDropoffLocationIds` instead.
   */
  dropoffLocationId?: string | null;
  /** Per-linked-truck dropoff location ids from the company's dropoff addresses. */
  truckDropoffLocationIds?: LeadTruckDropoffLocationIds;
  /** `pickup_locations` doc id for the optional business address. */
  businessAddressId?: string | null;
  financingStatus: string;
  timeFrame: string;
  vehicleInterest: string;
  truckIds: string[];
  truckRequest: TruckRequest;
  followUpNotes: FollowUpNote[];
  statusHistory: StatusHistory[];
  applicationIds: string[];
  createdAt: Date;
  updatedAt: Date;
  lastContactDate: Date | null;
  nextFollowUpDate: Date | null;
  source: string | null;
  priority: "low" | "medium" | "high";
  tags: string[];
  createdBy: string;
  lastActionDate: Date | null;
  emailHistory?: EmailHistory[];
  /** Set when shipping tab requests origin/dropoff locations for linked trucks. */
  needs_shipping_information?: boolean;
  /** Latest shipping quote snapshot saved when notifying subscribers. */
  shipping_quote_snapshot?: LeadShippingQuoteSnapshot | null;
  /** Incremented each time a shipping quote notification is requested. */
  shipping_quote_notification_token?: number;
}

export interface LeadShippingQuoteTruck {
  vehicleId: string;
  vehicleLabel: string;
  stockNumber: string;
  vinLast4: string;
  inventoryAddress: string;
  currentLocationAddress: string;
  destinationAddress: string;
  inventoryToCurrentMiles: number | null;
  inventoryToCurrentCost: number | null;
  currentToDestinationMiles: number | null;
  currentToDestinationCost: number | null;
  miles: number | null;
  shippingCost: number | null;
}

export interface LeadShippingQuoteSnapshot {
  pricePerMile: number;
  calculatedAt: string;
  quotedCount: number;
  totalMiles: number;
  totalShippingCost: number;
  trucks: LeadShippingQuoteTruck[];
}

/** Lead payload for ADMIN_LEAD_SHIPPING_QUOTE_COMPLETE notifications. */
export type LeadShippingQuoteCompleteNotification = Lead & {
  shipping_quote_snapshot: LeadShippingQuoteSnapshot;
};

/** Truck included in a lead shipping-information notification. */
export interface LeadShippingLocationTruck {
  vehicleId: string;
  stockNumber?: string | null;
  year?: number | string | null;
  make?: string | null;
  model?: string | null;
  vin?: string | null;
  missingOrigin: boolean;
  missingDropoff: boolean;
}

/** Lead payload for ADMIN_LEAD_SHIPPING_INFORMATION_NEEDED notifications. */
export type LeadShippingInformationNotification = Lead & {
  notification_trucks_needing_locations: LeadShippingLocationTruck[];
};

export interface LeadCustomerRecord {
  id: string;
  displayName: string;
  email?: string;
  phone?: string;
  userId?: string;
  leads: Lead[];
  leadCompanies: string[];
  soldVehicles: Vehicle[];
}
